/**
 * Два состояния любого экрана, который что-то грузит: «ещё едет» и «не
 * доехало».
 *
 * ⚠️ **Заведено по итогам ревью 29.08.2026, и заведено ради одного приёма на
 * все экраны.** На четырёх соседних экранах (группы, карточка группы, лента
 * сообщений, карточка сообщения) было три разных обхождения с отказом: где-то
 * кнопка «Повторить», где-то только красная строка, а у списка групп ветки
 * стояли так, что отказ печатался, а «Загрузка…» оставалась на экране навсегда,
 * и выход был только перезагрузкой страницы. Сводить это к одному дешевле, пока
 * экранов четыре, а не двадцать.
 *
 * Правило, ради которого компонента два, а не один: **отказ и ожидание не
 * показываются вместе**. Ждать нечего, если уже отказано, а красная строка
 * рядом с «Загрузка…» читается как «оно всё-таки грузится».
 *
 * ⚠️ **У отказа загрузки кнопка обязательна.** Сеть в наших условиях рвётся
 * штатно (раздел 3 CLAUDE.md), и экран без повтора лечится только F5, то есть
 * потерей всего набранного на нём. Кнопка тихая (`btn btn--quiet`): это не
 * действие страницы, а починка её собственной неудачи.
 *
 * Живёт в `lib/`, а не в фиче, потому что читают его и группы (ленивый чанк), и
 * рассылка (стартовый). Веса это не добавляет: разметки здесь две строки, а
 * стили общие.
 *
 * ⚠️ **С 14.09.2026 ожидание в продукте одно, и оно здесь.** До этого дня видов
 * было пять: этот компонент (7 мест), 43 написанных руками абзаца «Загрузка…» с
 * пятью разными формулировками на `Suspense`, своё колесо у озвучки и у вопросов
 * к записи, экран шагов входа в комнату и `<Note text="Загрузка…">`. Колесо,
 * слово и порог показа собраны сюда; замок на то, что шестого вида не заведут,
 * стоит в `lib/empty-wait.test.ts`.
 */

/**
 * Данные ещё едут.
 *
 * ⚠️ **Порог в 400 мс живёт в CSS (`.wait`), а не здесь.** Дело не во вкусе:
 * задержка, поставленная таймером в компоненте, обходится написанием своего
 * ожидания рядом, а правило на классе обойти нечем. Ниже порога не рисуется
 * ничего, в том числе для читалки: `visibility: hidden` убирает узел и с
 * экрана, и из дерева доступности. Мелькнувшее на 80 мс кольцо хуже, чем его
 * отсутствие.
 *
 * `text` пишется только там, где факт ДРУГОЙ («Архив готовится, обновим
 * сами…»), а не ради разнообразия: пять формулировок на одно и то же и были той
 * поломкой, которую этот компонент чинит. Половина из них вдобавок называла
 * наши внутренние части («палитра», «редактор»), которых человек снаружи не
 * видел ни разу.
 */
export function Loading(props: { text?: string; size?: "sm" | "lg" }) {
  const size = props.size === undefined ? "" : ` spin--${props.size}`;
  return (
    <p className="wait wait--ring muted" role="status">
      {/* Колесо пустое для читалки: смысл несёт слово рядом */}
      <span className={`spin${size}`} aria-hidden="true" />
      {props.text ?? "Загрузка…"}
    </p>
  );
}

/**
 * Кольцо строчным узлом, в строке текста: перед словом, которое уже стоит в
 * строке и меняется само (баннер озвучки, 07.10.2026: «Ждём очереди»,
 * «Озвучиваем 3 из 12»), либо перед своим `text` (кнопка «Озвучить» в окне
 * заготовки). `Loading` туда не годится: это абзац, его слово живёт в
 * `role="status"`, вставленном вместе с ним, а в строке баннера читалка
 * зачитывала бы каждую смену счёта.
 *
 * ⚠️ **Для читалки узла нет**: слово для неё ставит вызывающий в постоянную
 * живую область, которая стоит в документе и пустой (область, вставленная
 * вместе с текстом, часто не объявляется вовсе).
 *
 * ⚠️ Порог тот же `.wait`, только на обёртке, а не на кольце: у `.spin` своя
 * `animation`, и на одном узле она перебила бы показ по порогу, то есть кольцо
 * осталось бы `visibility: hidden` навсегда (замок в `lib/empty-wait.test.ts`).
 * Скрытый до порога узел место занимает, поэтому строка рядом не прыгает в миг,
 * когда кольцо показалось.
 */
export function LoadingRing(props: { text?: string }) {
  return (
    <span className="wait wait--inline" aria-hidden="true">
      <span className="spin" />
      {props.text}
    </span>
  );
}

/**
 * Ожидание того, что приедет СПИСКОМ: скелет строк вместо слова.
 *
 * Почему не кольцо. Кольцо говорит «идёт работа» и ничего не говорит о том, что
 * будет на этом месте; строки говорят и то, и другое, и при подмене на
 * настоящие данные экран не прыгает. Ровно поэтому скелет только у таблиц: там,
 * где приедет абзац или карточка, он обещал бы не ту форму.
 *
 * ⚠️ **Без мерцания.** Бегущий блик это движение ради движения: он не отвечает
 * ни «сколько осталось», ни «идёт ли ещё», а на 128 кбит/с (раздел 3.1
 * CLAUDE.md) крутится ровно столько же, сколько всё остальное не едет.
 *
 * Порог показа тот же, и по тому же правилу: класс `.wait` на самом блоке.
 *
 * ⚠️ **Скелет рисуется по колонкам СВОЕГО списка** (14.09.2026, долг подхода
 * 2з). До этого дня он ставил одну полоску в строку без шаблона колонок вовсе,
 * и получалась заготовка не той формы: одна широкая полоса вместо пяти узких,
 * после чего приехавшая таблица перекладывала экран целиком. Ради этого и
 * затевался скелет, поэтому промах был не косметический.
 *
 * `list` это тот же модификатор, которым экран одевает настоящий список
 * (`list--students`), а `cells` это сколько в нём колонок. Два довода, а не
 * один: число колонок живёт в CSS переменной `--cols`, и из js его не видно.
 *
 * ⚠️ `narrow` это та же метка, что на настоящем списке (`data-rows`), и
 * передаётся она по той же причине, что и разрез: скелет обязан быть ТОЙ ЖЕ
 * ФОРМЫ, что и приехавшая строка. У переехавшего на узкую строку списка форма
 * ниже порога колонок другая, две полоски вместо пяти, и скелет без метки
 * перекладывал бы экран ровно тем движением, ради отмены которого рисуется.
 *
 * ⚠️ **Значение, а не «да или нет», и с 17.09.2026 это не украшение.** Меток у
 * узкой строки две (`page` и `panel`, разбор в `styles.css`), и булев проп умел
 * назвать только первую: список уроков, переехавший на `panel`, метки не
 * получал вовсе и ждал семиполосной стопкой в 116 px то, что приезжало строкой
 * в 69 (замерено на 390). Скелет не той формы это ровно тот промах, ради
 * которого он и рисуется.
 * Значением такой формы не существует: неназванной механики больше нет.
 *
 * Пару «разрез и метка» сторожит `lib/list-cols.test.ts` тем же способом, каким
 * сторожит пару «разрез и число колонок»: сверкой с настоящим списком того же
 * экрана.
 */
export function LoadingRows(props: {
  list?: string;
  cells?: number;
  rows?: number;
  /** Механика узкой строки того же списка: `data-rows` на его `.list`. */
  narrow?: "page" | "panel";
}) {
  const rows = props.rows ?? 5;
  const cells = props.cells ?? 1;
  return (
    <ul
      className={props.list === undefined ? "list wait" : `list ${props.list} wait`}
      data-rows={props.narrow}
      role="status"
      aria-label="Загрузка…"
    >
      {Array.from({ length: rows }, (_, i) => (
        <li className="row" key={i} aria-hidden="true">
          {Array.from({ length: cells }, (_, j) => (
            <i className="skel" key={j} />
          ))}
        </li>
      ))}
    </ul>
  );
}

/**
 * Данные не доехали, и попробовать ещё раз можно отсюда.
 *
 * `text` собирается словарём отказов своей фичи (`groupErrorText`,
 * `announcementErrorText`), а не берётся у `Error.message`: код отказа знает
 * фича, а этот компонент знает только то, что показать надо строку и кнопку.
 */
export function LoadFailed(props: { text: string; onRetry: () => void }) {
  return (
    <p className="err">
      {props.text}{" "}
      <button type="button" className="btn btn--quiet" onClick={props.onRetry}>
        Повторить
      </button>
    </p>
  );
}
